import { useCallback, useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const ROLE_OPTIONS = [
  { value: '', label: 'No access' },
  { value: 'compliance_officer', label: 'Compliance officer' },
  { value: 'compliance_analyst', label: 'Compliance analyst' },
  { value: 'department_lead', label: 'Department lead' },
  { value: 'department_user', label: 'Department user' },
];
const NEEDS_DEPARTMENT = new Set(['department_lead', 'department_user']);

/**
 * Staff access — who on the TPA's staff can work in which client, and as
 * what. One row per person, one column per client. A change in a cell takes
 * effect on the person's next request; removing access ends any open session
 * in that client immediately.
 */
export default function StaffAccessTab({ showToast }) {
  const [data, setData] = useState(null);
  const [depts, setDepts] = useState({}); // clientId -> [{id,name}]
  const [cell, setCell] = useState(null); // { identityId, clientId, user_type, department_id, extra: [] }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/v2/tpa/staff`, { credentials: 'include' });
      const d = await res.json();
      if (!d.success) { setData({ staff: [], clients: [] }); return; }
      setData(d);
      const entries = await Promise.all(d.clients.map(async (c) => {
        try {
          const r = await fetch(`${API}/api/admin/v2/orgs/${c.id}/departments`, { credentials: 'include' });
          const j = await r.json();
          return [c.id, j.success ? (j.departments || []) : []];
        } catch { return [c.id, []]; }
      }));
      setDepts(Object.fromEntries(entries));
    } catch {
      setData({ staff: [], clients: [] });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const accountFor = (person, clientId) =>
    (person.accounts || []).find((a) => a.client_id === clientId && a.status === 'active') || null;

  const openCell = (person, client) => {
    const a = accountFor(person, client.id);
    setCell({
      identityId: person.id,
      personName: person.name,
      clientId: client.id,
      clientName: client.name,
      user_type: a?.user_type || '',
      department_id: a?.department_id || '',
      extra: (a?.also_departments || []).map((d) => d.id),
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (!cell.user_type) {
        const res = await fetch(`${API}/api/admin/v2/tpa/clients/${cell.clientId}/staff/${cell.identityId}`, {
          method: 'DELETE', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
        });
        const d = await res.json();
        if (!d.success) { showToast('error', 'Could not remove access', d.message); return; }
        showToast('success', 'Access removed', `${cell.personName} no longer works in ${cell.clientName}.`);
      } else {
        const res = await fetch(`${API}/api/admin/v2/tpa/clients/${cell.clientId}/staff/${cell.identityId}`, {
          method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_type: cell.user_type,
            department_id: NEEDS_DEPARTMENT.has(cell.user_type) ? (cell.department_id || null) : null,
            additional_department_ids: NEEDS_DEPARTMENT.has(cell.user_type) ? cell.extra : [],
          }),
        });
        const d = await res.json();
        if (!d.success) { showToast('error', 'Could not save access', d.message); return; }
        showToast('success', 'Access saved', `${cell.personName} works in ${cell.clientName} as ${ROLE_OPTIONS.find((r) => r.value === cell.user_type)?.label.toLowerCase()}.`);
      }
      setCell(null);
      await load();
    } catch (err) {
      showToast('error', 'Could not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  if (data === null) return <div className="text-sm text-slate-400">Loading…</div>;

  const clients = data.clients || [];
  const staff = data.staff || [];
  const cellDepts = cell ? (depts[cell.clientId] || []) : [];

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-lg font-semibold text-white">Staff access</h2>
        <p className="text-sm text-slate-400 max-w-2xl">
          Choose which clients each person works in, and their role there. A person can hold a different role in each client. Removing access takes effect right away.
        </p>
      </div>

      {clients.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-700 px-6 py-10 text-center text-sm text-slate-400">Add a client first.</div>
      ) : staff.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-700 px-6 py-10 text-center text-sm text-slate-400">Add your staff under People first.</div>
      ) : (
        <div className="rounded-2xl border border-slate-700/60 overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead className="bg-slate-900/40 border-b border-slate-700/50">
              <tr>
                <th className="text-left text-xs uppercase tracking-wide text-slate-400 font-semibold px-5 py-3">Person</th>
                {clients.map((c) => (
                  <th key={c.id} className="text-left text-xs uppercase tracking-wide text-slate-400 font-semibold px-5 py-3">
                    {c.name}{c.cms_contract_number ? <span className="block text-[10px] font-mono text-slate-500 normal-case tracking-normal">{c.cms_contract_number}</span> : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {staff.map((p) => (
                <tr key={p.id} className="border-b border-slate-700/30">
                  <td className="px-5 py-3.5">
                    <div className="text-sm font-medium text-white">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.email}{p.status === 'invited' ? ' · invite pending' : ''}</div>
                  </td>
                  {clients.map((c) => {
                    const a = accountFor(p, c.id);
                    return (
                      <td key={c.id} className="px-5 py-3.5">
                        <button
                          type="button"
                          onClick={() => openCell(p, c)}
                          className={`text-left rounded-lg px-3 py-2 border text-sm transition ${
                            a ? 'border-sky-500/40 bg-sky-500/10 text-sky-100 hover:bg-sky-500/20'
                              : 'border-slate-700 text-slate-500 hover:text-slate-300 hover:border-slate-500'
                          }`}
                        >
                          {a ? (
                            <>
                              <span className="block">{ROLE_OPTIONS.find((r) => r.value === a.user_type)?.label || a.user_type}</span>
                              {a.department_name && <span className="block text-xs text-sky-300/80">{a.department_name}{a.also_departments?.length ? ` +${a.also_departments.length}` : ''}</span>}
                            </>
                          ) : 'No access'}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {cell && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4">
          <form onSubmit={save} className="w-full max-w-md rounded-2xl bg-slate-800 border border-slate-600 shadow-2xl p-6 space-y-4">
            <div>
              <h3 className="text-base font-semibold text-white">{cell.personName}</h3>
              <p className="text-sm text-slate-400">in {cell.clientName}</p>
            </div>
            <label className="block">
              <span className="block text-xs uppercase tracking-wide text-slate-400 mb-1">Role in this client</span>
              <select value={cell.user_type} onChange={(e) => setCell({ ...cell, user_type: e.target.value, department_id: '', extra: [] })}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white focus:border-sky-500 focus:outline-none">
                {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>
            {NEEDS_DEPARTMENT.has(cell.user_type) && (
              <>
                <label className="block">
                  <span className="block text-xs uppercase tracking-wide text-slate-400 mb-1">Department</span>
                  {cellDepts.length === 0 ? (
                    <p className="text-sm text-amber-300">This client has no departments yet. Set them up under Clients → Departments &amp; people.</p>
                  ) : (
                    <select value={cell.department_id} required onChange={(e) => setCell({ ...cell, department_id: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white focus:border-sky-500 focus:outline-none">
                      <option value="">Choose…</option>
                      {cellDepts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  )}
                </label>
                {cellDepts.length > 1 && (
                  <div>
                    <span className="block text-xs uppercase tracking-wide text-slate-400 mb-1">Also oversees</span>
                    <div className="flex flex-wrap gap-2">
                      {cellDepts.filter((d) => d.id !== cell.department_id).map((d) => {
                        const on = cell.extra.includes(d.id);
                        return (
                          <button key={d.id} type="button"
                            onClick={() => setCell({ ...cell, extra: on ? cell.extra.filter((x) => x !== d.id) : [...cell.extra, d.id] })}
                            className={`text-xs px-2.5 py-1 rounded-full border transition ${on ? 'bg-sky-500/20 border-sky-500/50 text-sky-100' : 'border-slate-600 text-slate-400 hover:text-white'}`}>
                            {d.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button type="button" onClick={() => setCell(null)} className="text-sm text-slate-400 hover:text-white">Cancel</button>
              <button type="submit" disabled={busy || (NEEDS_DEPARTMENT.has(cell.user_type) && !cell.department_id)}
                className="px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium disabled:opacity-50">
                {busy ? 'Saving…' : cell.user_type ? 'Save access' : 'Remove access'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
